import { ONLINE_PAGES_SHOWN, onlineQuery, type OnlineAnswer, type OnlinePlayer } from "odyssey-shared";
import { say } from "../../words.ts";
import type { OnlineFetch, OnlineReply } from "./online-client.ts";

export type { OnlineFetch, OnlineReply } from "./online-client.ts";

/**
 * The page's model of who is online (ADR-056's amendment of 2026-10-06, decisions 2, 3, 5 and 7), owned by the page
 * rather than by React, on the desk's pattern: pure logic over a clock and a fetch it is given.
 *
 * - **Asking.** At once when the window opens, when the tab shows again and when an action of the GM's own was
 *   written (`refresh`); then every 10 seconds, each wait moved by up to 2 seconds either way so several GMs do not
 *   ask together; never while the window is closed or the tab hidden. A refresh reads again each page the GM opened,
 *   one request each, four at most.
 * - **Searching.** The model waits 300 ms after the last key, keeps one request in flight and starts at most one
 *   search a second; an answer to a question a newer one has overtaken is dropped. While a search is being typed
 *   the refresh waits. While the field holds a search the shared rule refuses, nothing is asked, by a search or a
 *   refresh, and the list matches no one.
 * - **Pages.** Show more asks for the page after the last, up to `ONLINE_PAGES_SHOWN`; past that the list says to
 *   search. A row that a join between two pages repeats is held once.
 * - **Failures.** A failure keeps the rows, says so and doubles the wait from 10 seconds, to 60 at most; one the page
 *   cannot read waits 60 at once; over a limit waits at least as long as the gateway asked. A 401 or a 403 (a session
 *   ended, a role removed while the window is open) stops the asking and empties the list until the window opens
 *   again.
 * - **Selection.** Kept by family across refreshes, so it follows a player who changes map; a family that leaves
 *   stays selected, and the list says whether it is listed.
 */

/**
 * The wait after the last key before a search is asked.
 */
export const ONLINE_SEARCH_WAIT_MS = 300;

/**
 * The least time between the starts of two searches.
 */
export const ONLINE_SEARCH_GAP_MS = 1000;

/**
 * The wait between two refreshes, before it is moved.
 */
export const ONLINE_REFRESH_MS = 10_000;

/**
 * The most a refresh's wait is moved either way.
 */
export const ONLINE_REFRESH_JITTER_MS = 2_000;

/**
 * The longest wait after a failure.
 */
export const ONLINE_BACKOFF_MAX_MS = 60_000;

/**
 * The timers the model runs on: the clock, and a call after a wait that can be cancelled.
 */
export interface OnlineTimers {
  readonly now: () => number;
  readonly after: (ms: number, run: () => void) => () => void;
}

/**
 * What the model runs with: the fetch, the timers, and where the refresh's move comes from, a number from 0 to 1
 * (`Math.random` unless a test gives another).
 */
export interface OnlineModelOptions {
  readonly fetch: OnlineFetch;
  readonly timers: OnlineTimers;
  readonly jitter?: () => number;
}

/**
 * One group of the list: a map, its header's words, its count of matches and the rows held.
 */
export interface OnlineGroup {
  readonly id: string;
  readonly header: string;
  readonly count: number;
  readonly players: readonly OnlinePlayer[];
}

/**
 * What the list shows: the search as typed; the groups; the matches in all and the rows held; whether Show more may
 * ask and whether the list has shown its last page and says to search; whether it is reading with nothing to show;
 * the line under the search (reading, no match, nobody, a failure, a limit, a session or a role that ended) or null;
 * the family selected, kept as it was named, and whether it is listed; and each listed map's name by its id.
 */
export interface OnlineSnapshot {
  readonly text: string;
  readonly groups: readonly OnlineGroup[];
  readonly total: number;
  readonly held: number;
  readonly more: boolean;
  readonly capped: boolean;
  readonly reading: boolean;
  readonly line: string | null;
  readonly selected: string | null;
  readonly selectedListed: boolean;
  readonly mapNames: Readonly<Record<string, string>>;
}

/**
 * Why the list cannot show what it holds, or null when it can.
 */
type Trouble = "failed" | "rate" | "session" | "forbidden" | null;

/**
 * A read the model starts: a search for the typed question, the page after the last, or a refresh of the pages
 * open.
 */
type Read = "search" | "more" | "refresh";


/**
 * A map's header in the list: its name, with its group's when it has one, led by "Here" for the GM's own map.
 *
 * @param name The map's name.
 * @param group The group's name, or null for a map in none.
 * @param own Whether the GM is on this map.
 * @returns The header, "Here: Thornwood, North".
 */
export function headerWords(name: string, group: string | null, own: boolean): string {
  const header = group === null ? name : say("moderation.online.grouped", { name, group });
  return own ? say("moderation.online.here", { header }) : header;
}

/**
 * The model.
 */
export class OnlineModel {
  private readonly listeners = new Set<() => void>();
  private readonly jitter: () => number;
  private text = "";
  private asked: string | null = null;
  private pages: readonly OnlineAnswer[] = [];
  private answered = false;
  private isOpen = false;
  private visible = true;
  private stopped = false;
  private trouble: Trouble = null;
  private failures = 0;
  private question = 0;
  private searchDue: number | null = null;
  private lastSearch = Number.NEGATIVE_INFINITY;
  private moreAsked = false;
  private refreshAt = Number.NEGATIVE_INFINITY;
  private flight: Promise<void> | null = null;
  private cancelWake: (() => void) | null = null;
  private selected: string | null = null;
  private snapshot: OnlineSnapshot;

  /**
   * @param options The fetch, the timers and the refresh's move.
   */
  public constructor(private readonly options: OnlineModelOptions) {
    this.jitter = options.jitter ?? Math.random;
    this.snapshot = this.view();
  }

  /**
   * Hears that the snapshot changed. Works unbound, as React calls it.
   *
   * @param listener Called on each change.
   * @returns Stops hearing.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the list shows now. Works unbound, as React calls it.
   *
   * @returns The snapshot, the same object until something changes.
   */
  public readonly getSnapshot = (): OnlineSnapshot => this.snapshot;

  /**
   * The window opened: the list asks at once, a 401 or a 403 met before is tried again.
   */
  public readonly open = (): void => {
    this.isOpen = true;
    this.stopped = false;
    if (this.trouble === "session" || this.trouble === "forbidden") {
      this.trouble = null;
    }
    this.refreshAt = this.options.timers.now();
    this.changed();
    this.pump();
  };

  /**
   * The window closed: nothing more is asked; what is held stays.
   */
  public readonly close = (): void => {
    this.isOpen = false;
    this.sleep();
  };

  /**
   * The tab was hidden or shown again: hidden asks nothing, shown asks at once.
   *
   * @param visible Whether the tab shows.
   */
  public readonly shown = (visible: boolean): void => {
    this.visible = visible;
    if (!visible) {
      this.sleep();
      return;
    }
    this.refreshAt = this.options.timers.now();
    this.pump();
  };

  /**
   * The search field changed: the question is asked once the keys stop.
   *
   * @param text The field's text.
   */
  public readonly type = (text: string): void => {
    if (text === this.text) {
      return;
    }
    this.text = text;
    this.question += 1;
    this.searchDue = this.options.timers.now() + ONLINE_SEARCH_WAIT_MS;
    this.changed();
    this.pump();
  };

  /**
   * Show more: the page after the last, while fewer than `ONLINE_PAGES_SHOWN` are open.
   */
  public readonly more = (): void => {
    if (!this.snapshot.more) {
      return;
    }
    this.moreAsked = true;
    this.pump();
  };

  /**
   * Asks again at once, as after an action of the GM's own was written.
   */
  public readonly refresh = (): void => {
    this.refreshAt = this.options.timers.now();
    this.pump();
  };

  /**
   * Selects a family, or none.
   *
   * @param family The family as the list or the form names it, or null.
   */
  public readonly select = (family: string | null): void => {
    this.selected = family;
    this.changed();
  };

  /**
   * Settles once no request is in flight.
   *
   * @returns A promise that settles then.
   */
  public async idle(): Promise<void> {
    while (this.flight !== null) {
      await this.flight;
    }
  }

  /**
   * Starts the read that is due, or sets a wake-up for when one will be.
   */
  private pump(): void {
    if (!this.isOpen || !this.visible || this.stopped || this.flight !== null) {
      return;
    }
    const now = this.options.timers.now();
    if (this.searchDue !== null) {
      const at = Math.max(this.searchDue, this.lastSearch + ONLINE_SEARCH_GAP_MS);
      if (now < at) {
        this.wake(at);
        return;
      }
      this.start("search");
      return;
    }
    if (this.moreAsked) {
      this.start("more");
      return;
    }
    if (now < this.refreshAt) {
      if (Number.isFinite(this.refreshAt)) {
        this.wake(this.refreshAt);
      }
      return;
    }
    this.start("refresh");
  }

  /**
   * Starts one read and settles what it answered.
   *
   * @param read The read.
   */
  private start(read: Read): void {
    this.sleep();
    const question = this.question;
    const query = this.text === "" ? null : onlineQuery(this.text);
    if (read === "search") {
      this.searchDue = null;
      this.lastSearch = this.options.timers.now();
    }
    if (this.text !== "" && query === null) {
      this.asked = this.text;
      this.pages = [];
      this.answered = true;
      this.trouble = null;
      this.moreAsked = false;
      this.refreshAt = Number.POSITIVE_INFINITY;
      this.changed();
      return;
    }
    if (read === "more") {
      this.moreAsked = false;
    }
    const flight = this.run(read, question, query === null ? null : this.text).finally(() => {
      this.flight = null;
      this.changed();
      this.pump();
    });
    this.flight = flight;
    this.changed();
  }

  /**
   * Runs one read: a search asks the first page of the question; Show more the page after the last; a refresh asks
   * again each page open, one at a time, or the first page alone when the question has changed. An answer is kept
   * only while the question it answers is still the one typed.
   *
   * @param read The read.
   * @param question The question's number when the read started.
   * @param query The question as the gateway is asked it, or null for everyone.
   */
  private async run(read: Read, question: number, query: string | null): Promise<void> {
    if (read === "more") {
      const after = this.pages[this.pages.length - 1]?.next ?? null;
      if (after === null || this.pages.length >= ONLINE_PAGES_SHOWN) {
        return;
      }
      const reply = await this.options.fetch(this.asked, after);
      if (question === this.question && this.settled(reply)) {
        this.pages = [...this.pages, (reply as { readonly answer: OnlineAnswer }).answer];
        this.changed();
      }
      return;
    }
    const count = read === "refresh" && query === this.asked ? Math.max(1, Math.min(this.pages.length, ONLINE_PAGES_SHOWN)) : 1;
    const pages: OnlineAnswer[] = [];
    for (let page = 0; page < count; page += 1) {
      const after = page === 0 ? null : (pages[page - 1]?.next ?? null);
      if (page > 0 && after === null) {
        break;
      }
      const reply = await this.options.fetch(query, after);
      if (question !== this.question || this.searchDue !== null) {
        return;
      }
      if (!this.settled(reply)) {
        return;
      }
      pages.push((reply as { readonly answer: OnlineAnswer }).answer);
    }
    this.asked = query;
    this.pages = pages;
    this.answered = true;
    this.changed();
  }

  /**
   * Settles the trouble and the next refresh a reply leaves.
   *
   * @param reply The reply.
   * @returns True for an answer, which the caller keeps.
   */
  private settled(reply: OnlineReply): boolean {
    const now = this.options.timers.now();
    if (reply.kind === "ok") {
      this.failures = 0;
      this.trouble = null;
      this.refreshAt = now + ONLINE_REFRESH_MS + Math.round((this.jitter() * 2 - 1) * ONLINE_REFRESH_JITTER_MS);
      return true;
    }
    if (reply.kind === "session" || reply.kind === "forbidden") {
      this.stopped = true;
      this.trouble = reply.kind;
      this.pages = [];
      this.moreAsked = false;
      this.searchDue = null;
      this.changed();
      return false;
    }
    this.failures += 1;
    const backoff = Math.min(ONLINE_BACKOFF_MAX_MS, ONLINE_REFRESH_MS * 2 ** this.failures);
    this.trouble = reply.kind === "rate" ? "rate" : "failed";
    this.refreshAt = now + (reply.kind === "unreadable" ? ONLINE_BACKOFF_MAX_MS : reply.kind === "rate" ? Math.max(reply.retryAfterMs, backoff) : backoff);
    this.changed();
    return false;
  }

  /**
   * Sets the one wake-up, replacing any before it.
   *
   * @param at When to wake.
   */
  private wake(at: number): void {
    this.sleep();
    this.cancelWake = this.options.timers.after(Math.max(0, at - this.options.timers.now()), () => {
      this.cancelWake = null;
      this.pump();
    });
  }

  /**
   * Cancels the wake-up, if one is set.
   */
  private sleep(): void {
    this.cancelWake?.();
    this.cancelWake = null;
  }

  /**
   * Works the snapshot out again and tells the listeners.
   */
  private changed(): void {
    this.snapshot = this.view();
    for (const listener of this.listeners) {
      listener();
    }
  }

  /**
   * The snapshot of what is held now.
   *
   * @returns The snapshot.
   */
  private view(): OnlineSnapshot {
    const last = this.pages[this.pages.length - 1];
    const seen = new Set<string>();
    const rows: OnlinePlayer[] = [];
    for (const page of this.pages) {
      for (const player of page.players) {
        const key = player.family.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          rows.push(player);
        }
      }
    }
    const maps = last?.maps ?? [];
    const own = last?.own ?? null;
    const groups: OnlineGroup[] = maps.map((map) => ({ id: map.id, header: headerWords(map.name, map.group, map.id === own), count: map.count, players: rows.filter((row) => row.map === map.id) }));
    const strays = rows.filter((row) => !maps.some((map) => map.id === row.map));
    for (const id of [...new Set(strays.map((row) => row.map))]) {
      const players = strays.filter((row) => row.map === id);
      groups.push({ id, header: headerWords(id, null, id === own), count: players.length, players });
    }
    const reading = this.flight !== null && !this.answered;
    const next = last?.next ?? null;
    const empty = this.answered && this.trouble === null && rows.length === 0 ? (this.asked === null ? say("moderation.online.nobody") : say("moderation.online.noMatch")) : null;
    const line = this.trouble !== null ? say(`moderation.online.${this.trouble}`) : reading ? say("moderation.online.reading") : empty;
    const selected = this.selected;
    return {
      text: this.text,
      groups: groups.filter((group) => group.players.length > 0),
      total: last?.total ?? 0,
      held: rows.length,
      more: next !== null && this.pages.length < ONLINE_PAGES_SHOWN && !this.stopped,
      capped: next !== null && this.pages.length >= ONLINE_PAGES_SHOWN,
      reading,
      line,
      selected,
      selectedListed: selected !== null && seen.has(selected.toLowerCase()),
      mapNames: Object.fromEntries(maps.map((map) => [map.id, map.name])),
    };
  }
}
